import { useMemo } from "react";
import type {
  AccountDto,
  FilterDto,
  LabelMapDto,
  MessageSummaryDto,
  VirtualFolderDto,
} from "../../../shared/types";
import { MessageListItem } from "./MessageListItem";
import { avatarFor } from "./MessageList";
import { OTHER_FILTER_ID, SWATCH_CLASSES } from "../filterColors";
import { useVirtualFolderMessages, useVirtualFolders } from "../../hooks/useApi";

/** A column definition for the board: a real filter, Other, Inbox, or a folder. */
interface ColumnDef {
  key: string;
  name: string;
  color: string; // FilterColor key into SWATCH_CLASSES
}

/**
 * Row-1 grouping: enabled filters in sidebar order, each with its member
 * messages, plus the synthetic "Other" bucket (no label). Multi-label mail
 * appears in every matching group — same semantics as the sidebar counts.
 */
export function groupByFilter(
  messages: MessageSummaryDto[],
  labels: LabelMapDto,
  enabledFilters: FilterDto[],
): { filter: ColumnDef & { id: number }; messages: MessageSummaryDto[] }[] {
  const byId = new Map<number, MessageSummaryDto[]>(enabledFilters.map((f) => [f.id, []]));
  const other: MessageSummaryDto[] = [];
  for (const m of messages) {
    const ids = labels[m.id] ?? [];
    if (ids.length === 0) other.push(m);
    for (const id of ids) byId.get(id)?.push(m);
  }
  return [
    ...enabledFilters.map((f) => ({
      filter: { key: `f${f.id}`, id: f.id, name: f.name, color: f.color },
      messages: byId.get(f.id)!,
    })),
    {
      filter: { key: "other", id: OTHER_FILTER_ID, name: "Other", color: "gray" },
      messages: other,
    },
  ];
}

/** Row-2 order: most unread first; ties keep Inbox first, then folder order. */
export function sortFoldersByUnread(folders: VirtualFolderDto[]): VirtualFolderDto[] {
  return [...folders].sort(
    (a, b) => b.unreadCount - a.unreadCount || a.order - b.order,
  );
}

export function AnalyzeTab({
  accountId,
  filters,
  labels,
  accounts,
  messages,
  hiddenAccountIds,
  selectedId,
  onOpenMessage,
}: {
  accountId: number;
  filters: FilterDto[];
  labels: LabelMapDto;
  accounts: AccountDto[];
  /** Flattened loaded inbox pages of the current view. */
  messages: MessageSummaryDto[];
  hiddenAccountIds: Set<number>;
  selectedId: string | null;
  onOpenMessage: (id: string) => void;
}) {
  const { data: virtualFolders } = useVirtualFolders(true);

  // Loaded inbox mail minus hidden accounts, newest first (pages interleave
  // accounts, so an explicit sort is required).
  const visible = useMemo(
    () =>
      messages
        .filter((m) => !(m.origin && hiddenAccountIds.has(m.origin.accountId)))
        .sort(
          (a, b) =>
            new Date(b.receivedDateTime).getTime() - new Date(a.receivedDateTime).getTime(),
        ),
    [messages, hiddenAccountIds],
  );

  const enabledFilters = useMemo(
    () => filters.filter((f) => f.enabled).sort((a, b) => a.order - b.order),
    [filters],
  );
  const groups = useMemo(
    () => groupByFilter(visible, labels, enabledFilters),
    [visible, labels, enabledFilters],
  );
  const sortedFolders = useMemo(
    () => sortFoldersByUnread(virtualFolders ?? []),
    [virtualFolders],
  );
  const inboxUnread = visible.filter((m) => !m.isRead).length;

  const card = (m: MessageSummaryDto) => (
    <MessageListItem
      key={m.id}
      message={m}
      selected={m.id === selectedId}
      matchedFilters={(labels[m.id] ?? [])
        .map((id) => filters.find((f) => f.id === id))
        .filter((f): f is FilterDto => f !== undefined)}
      avatar={avatarFor(m, accounts, accountId)}
      onClick={() => onOpenMessage(m.id)}
    />
  );

  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      {/* Header bar — actions land here later. */}
      <div className="flex h-9 shrink-0 items-center gap-2 border-b border-stroke bg-white px-3">
        <span className="text-[13px] font-semibold">Analyze emails</span>
        <span className="text-[11px] text-ink-muted">
          All your mail at a glance — AI groups on top, folders below.
        </span>
      </div>

      <div className="grid min-h-0 flex-1 grid-rows-2">
        {/* Row 1: one column per AI group. */}
        <div className="flex min-h-0 gap-3 overflow-x-auto border-b border-stroke bg-surface p-3">
          {groups.map(({ filter, messages: groupMessages }) => (
            <Column
              key={filter.key}
              def={filter}
              unread={groupMessages.filter((m) => !m.isRead).length}
              total={groupMessages.length}
            >
              {groupMessages.length === 0 ? (
                <Empty label="No mail in this group." />
              ) : (
                groupMessages.map(card)
              )}
            </Column>
          ))}
        </div>

        {/* Row 2: Inbox + virtual folders, most unread first. */}
        <div className="flex min-h-0 gap-3 overflow-x-auto bg-surface p-3">
          <Column
            def={{ key: "inbox", name: "Inbox", color: "blue" }}
            unread={inboxUnread}
            total={visible.length}
          >
            {visible.length === 0 ? <Empty label="Inbox is empty." /> : visible.map(card)}
          </Column>
          {sortedFolders.map((f) => (
            <FolderColumn key={f.id} folder={f} card={card} />
          ))}
        </div>
      </div>
    </div>
  );
}

/** One folder column; fetches its own message list (cached 60s). */
function FolderColumn({
  folder,
  card,
}: {
  folder: VirtualFolderDto;
  card: (m: MessageSummaryDto) => React.ReactNode;
}) {
  const query = useVirtualFolderMessages(folder.id);
  return (
    <Column
      def={{ key: `vf${folder.id}`, name: folder.name, color: folder.color }}
      unread={folder.unreadCount}
      total={folder.totalCount}
    >
      {query.isLoading ? (
        <Empty label="Loading…" />
      ) : query.error ? (
        <p className="px-2.5 py-2 text-[12px] text-red-700">{(query.error as Error).message}</p>
      ) : (query.data?.messages ?? []).length === 0 ? (
        <Empty label="Empty folder." />
      ) : (
        query.data!.messages.map(card)
      )}
    </Column>
  );
}

function Column({
  def,
  unread,
  total,
  children,
}: {
  def: ColumnDef;
  unread: number;
  total: number;
  children: React.ReactNode;
}) {
  return (
    <div className="flex min-h-0 w-[280px] shrink-0 flex-col overflow-hidden rounded border border-stroke bg-white">
      <div className="flex shrink-0 items-center gap-1.5 border-b border-stroke px-2.5 py-1.5">
        <span
          className={`size-2.5 shrink-0 rounded-full ${
            SWATCH_CLASSES[def.color as keyof typeof SWATCH_CLASSES] ?? SWATCH_CLASSES.gray
          }`}
        />
        <span className="min-w-0 flex-1 truncate text-[12px] font-semibold">{def.name}</span>
        <span className="shrink-0 rounded-full bg-black/5 px-1.5 text-[11px] text-ink-muted">
          {unread > 0 ? `${unread} · ${total}` : total}
        </span>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
    </div>
  );
}

function Empty({ label }: { label: string }) {
  return <p className="px-2.5 py-2 text-[12px] text-ink-muted">{label}</p>;
}
